import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, KeyRound, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ROLE_LABEL = { owner: "Owner (super admin)", office: "Office", tech: "Tech" };
const ROLE_TONE = {
  owner: { bg: "#181854", text: "#FFFFFF" },
  office: { bg: "#E0E7FF", text: "#181854" },
  tech: { bg: "#DBEAFE", text: "#1E40AF" },
};

function RolePill({ role }) {
  const c = ROLE_TONE[role] || { bg: "#E4E6EF", text: "#6A6E85" };
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {ROLE_LABEL[role] || role}
    </span>
  );
}

function UserModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    email: initial?.email || "",
    name: initial?.name || "",
    role: initial?.role || "office",
    password: "",
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.email.trim()) return setError("Email is required.");
    if (!form.name.trim()) return setError("Name is required.");
    if (!isEdit && (!form.password || form.password.length < 6))
      return setError("Password must be at least 6 characters.");
    if (isEdit && form.password && form.password.length < 6)
      return setError("New password must be at least 6 characters.");
    setBusy(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name,
          role: form.role,
          active: form.active,
        };
        if (form.password) payload.password = form.password;
        const r = await api.patch(`/users/${initial.id}`, payload);
        toast.success("User updated");
        onSaved(r.data);
      } else {
        const r = await api.post("/users", {
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          role: form.role,
          password: form.password,
          active: form.active,
        });
        toast.success("User created");
        onSaved(r.data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#101038]/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
        <h2 className="text-xl font-bold text-[#181854]">
          {isEdit ? "Edit user" : "New user"}
        </h2>
        <p className="text-sm text-[#6A6E85] mt-1">
          {isEdit
            ? "Change name, role, active state, or reset the password."
            : "Create a new GT Field OS account and assign a role."}
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">
              Email {!isEdit && <span className="text-[#DC2626]">*</span>}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={isEdit}
              data-testid="user-email"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white disabled:bg-[#F5F6FA]"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">
              Full name <span className="text-[#DC2626]">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="user-name"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">
              Role
            </label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="user-role"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
            >
              <option value="owner">Owner — super admin</option>
              <option value="office">Office — admin</option>
              <option value="tech">Tech — field</option>
            </select>
            <p className="text-[11px] text-[#6A6E85] mt-1">
              {form.role === "owner" && "Full access, including user management and invoicing."}
              {form.role === "office" && "Runs the office — scheduling, invoicing, clients."}
              {form.role === "tech" && "Field tech — sees only their own jobs on the mobile app."}
            </p>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">
              {isEdit ? "New password (leave blank to keep current)" : "Password"} {!isEdit && <span className="text-[#DC2626]">*</span>}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={isEdit ? "•••••••• (unchanged)" : "at least 6 characters"}
              data-testid="user-password"
              autoComplete="new-password"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#14162E]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="user-active"
              className="rounded border-[#E4E6EF]"
            />
            Active — can sign in
          </label>

          {error && <p className="text-sm text-[#DC2626]" data-testid="user-error">{error}</p>}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#ECEDF6] hover:bg-[#E4E6EF] text-[#181854] font-medium transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="user-submit"
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#181854] hover:bg-[#101038] text-white font-medium transition-colors duration-200 disabled:opacity-60"
            >
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoaded(false);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoaded(true));
  };

  useEffect(load, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Account disabled" : "Account enabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#181854] tracking-tight">Users</h1>
          <p className="text-[#6A6E85] mt-1">
            Add or remove GT Field OS accounts and set what each person can do.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="users-new-btn"
          className="inline-flex items-center gap-2 bg-[#181854] hover:bg-[#101038] text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200"
        >
          <Plus size={16} /> New user
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E4E6EF] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F6FA] border-b border-[#E4E6EF]">
              <tr className="text-left text-xs uppercase tracking-wider text-[#6A6E85]">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E6EF]">
              {!loaded && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6A6E85]">Loading…</td></tr>
              )}
              {loaded && users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6A6E85]">No users yet.</td></tr>
              )}
              {loaded && users.map((u) => {
                const isSelf = me?.id === u.id;
                return (
                  <tr key={u.id} data-testid={`users-row-${u.id}`} className="hover:bg-[#F5F6FA] transition-colors duration-200">
                    <td className="px-4 py-3 text-[#14162E] font-medium">
                      {u.name}
                      {isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#4F9CE0] font-bold">You</span>}
                    </td>
                    <td className="px-4 py-3 text-[#6A6E85]">{u.email}</td>
                    <td className="px-4 py-3"><RolePill role={u.role} /></td>
                    <td className="px-4 py-3">
                      {u.active ? (
                        <span className="inline-flex items-center gap-1.5 text-[#166534] text-xs font-semibold uppercase tracking-wider">
                          <ShieldCheck size={13} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[#6A6E85] text-xs font-semibold uppercase tracking-wider">
                          <ShieldOff size={13} /> Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing(u)}
                          data-testid={`users-edit-${u.id}`}
                          className="p-2 rounded-md hover:bg-[#ECEDF6] text-[#181854] transition-colors duration-200"
                          aria-label={`Edit ${u.name}`}
                          title="Edit / reset password"
                        >
                          <Pencil size={15} />
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() => toggleActive(u)}
                            data-testid={`users-toggle-${u.id}`}
                            className="p-2 rounded-md hover:bg-[#ECEDF6] text-[#6A6E85] transition-colors duration-200"
                            aria-label={u.active ? "Disable account" : "Enable account"}
                            title={u.active ? "Disable" : "Enable"}
                          >
                            {u.active ? <ShieldOff size={15} /> : <ShieldCheck size={15} />}
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            onClick={() => removeUser(u)}
                            data-testid={`users-delete-${u.id}`}
                            className="p-2 rounded-md hover:bg-[#FEE2E2] text-[#DC2626] transition-colors duration-200"
                            aria-label={`Delete ${u.name}`}
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-[#6A6E85] flex items-center gap-1.5">
        <KeyRound size={12} /> Tip: to reset someone&apos;s password, hit the pencil and enter a new one.
      </p>

      {creating && (
        <UserModal
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <UserModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
